import { motion, AnimatePresence } from 'motion/react';
import { X, BookOpen, Brain, Zap, Moon, Activity } from 'lucide-react';

interface AboutModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function AboutModal({ isOpen, onClose }: AboutModalProps) {
  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-slate-950/80 backdrop-blur-md"
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            className="bg-surface border-border-main relative max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-[2.5rem] border shadow-2xl"
          >
            {/* Header */}
            <div className="sticky top-0 z-10 flex items-center justify-between border-b bg-inherit px-8 py-6">
              <div className="flex items-center gap-3">
                <div className="bg-primary/10 flex h-10 w-10 items-center justify-center rounded-xl">
                  <BookOpen className="text-primary h-5 w-5" />
                </div>
                <div>
                  <h2 className="text-text-main text-xl font-black tracking-tight">
                    The MMEM Theory
                  </h2>
                  <p className="flex items-center gap-1.5 text-[10px] font-black tracking-widest text-amber-500 uppercase">
                    <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-amber-500" />
                    Unpublished Research Preview
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="rounded-full p-2 transition-colors hover:bg-slate-100 dark:hover:bg-slate-800"
              >
                <X className="h-6 w-6 text-slate-400" />
              </button>
            </div>

            {/* Content */}
            <div className="space-y-10 p-8 md:p-12">
              <section>
                <h3 className="text-text-main mb-4 text-2xl font-black tracking-tight">
                  Abstract
                </h3>
                <p className="leading-relaxed text-slate-600 dark:text-slate-400">
                  The Mojolagbe Memory Encoding Model (MMEM), authored by Jamiu
                  Mojolagbe, proposes a multi-variable linear-multiplicative
                  equation to predict the initial strength and subsequent
                  durability of information stored in long-term memory. It
                  categorizes learning into three distinct phases: Encoding,
                  Reinforcement, and Biological Consolidation.
                </p>
              </section>

              <section className="rounded-3xl bg-slate-900 p-8 text-white dark:bg-slate-950">
                <h3 className="text-primary mb-6 text-xs font-black tracking-[0.2em] uppercase">
                  The Core Equation
                </h3>
                <div className="mb-8 text-center text-3xl font-black tracking-tighter md:text-5xl">
                  L = (A · M · C) + (R · T) + S
                </div>
                <div className="grid grid-cols-1 gap-6 text-sm md:grid-cols-3">
                  <div className="space-y-2">
                    <div className="text-primary flex items-center gap-2 font-bold">
                      <Brain className="h-4 w-4" /> Encoding Triad
                    </div>
                    <p className="text-slate-400">
                      Attention (A), Mood (M), and Environmental Conduciveness
                      (C). A multiplicative bottleneck.
                    </p>
                  </div>
                  <div className="space-y-2">
                    <div className="text-secondary flex items-center gap-2 font-bold">
                      <Zap className="h-4 w-4" /> Reinforcement
                    </div>
                    <p className="text-slate-400">
                      Repetition (R) and Time Spacing (T). Acts as a power
                      multiplier for rehearsal via LTP.
                    </p>
                  </div>
                  <div className="space-y-2">
                    <div className="text-accent flex items-center gap-2 font-bold">
                      <Moon className="h-4 w-4" /> Consolidation
                    </div>
                    <p className="text-slate-400">
                      Sleep (S). A non-negotiable additive constant for
                      neurological stabilization.
                    </p>
                  </div>
                </div>
              </section>

              <section className="space-y-6">
                <div>
                  <h4 className="text-text-main flex items-center gap-2 text-lg font-bold">
                    <Activity className="text-primary h-5 w-5" />
                    The Mojolagbe-Ebbinghaus Synthesis
                  </h4>
                  <p className="mt-2 leading-relaxed text-slate-600 dark:text-slate-400">
                    The MMEM integrates with the classical Ebbinghaus Forgetting
                    Curve. By defining the mathematical &quot;Stability&quot;
                    variable (L), it determines the curve&quot;s starting height
                    and resistance to decay. As L increases, the rate of decay
                    slows significantly.
                  </p>
                </div>
                <div className="border-primary/20 border-l-4 pl-6 text-sm text-slate-500 italic">
                  &quot;Initial encoding is a bottleneck process. Optimal
                  environment and mood cannot compensate for a total lack of
                  attention.&quot; - Proposition 1
                </div>
              </section>

              <footer className="border-border-main flex flex-col justify-between gap-4 border-t pt-8 text-[10px] font-black tracking-[0.2em] text-slate-400 uppercase md:flex-row">
                <div>Author: Jamiu Mojolagbe</div>
                <div>Subject: Cognitive Psychology</div>
              </footer>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
